/**
 * **The drawn documents' page** (Stream C, Documents.dc.html), built literally and scored against
 * the drawing at 72 px to the inch (`tools/paper-fidelity.ts`): E01's Sheet as the frame, then
 * what each document engine hands it — the big line, blocks of dotted rows in the drawing's own
 * grids, the signature lines two to a row. A figure is cents and prints as `$ 387,728.12`.
 */
import type { ReactNode } from 'react';
import type { PaperCell, PaperPage } from '@cider/domain/engines';
import { Sheet, formatDate, formatDateTime, formatMoney, type SheetSize } from '@cider/ui';
import { renderSVG } from 'uqr';

/** The drawing's grids, by name (no inline styles): print.css holds each one's columns. */
const COLS: Record<string, string> = {
  '1fr': 'one',
  '1fr 160px': 'figure',
  '1fr 130px': 'figure-130',
  '1fr 1fr': 'two',
  '1fr 110px 1fr 110px': 'views',
  '1fr 100px 100px 100px': 'budget',
  '1fr 140px': 'figure-140',
  '1fr 140px 120px': 'paid',
  '1fr 120px 160px': 'voted',
  '1fr 120px': 'count',
  '1fr 120px 120px': 'two-years',
  '1fr 70px 90px 90px 1fr': 'lines',
  '60px 1fr 110px': 'month',
  '1fr 120px 100px 120px': 'tax',
  // Stream C · C12 (Rebuild 59): the commitment's figure, and a credit's what · source · credits.
  '1fr 110px': 'figure-110',
  '1fr 1fr 90px': 'two-figure',
  // Stream A (Paper.dc.html).
  '1fr 90px 90px 90px': 'counts',
  '70px 1fr 110px 120px': 'bags',
  '70px 1fr': 'label-70',
  '80px 1fr': 'label-80',
  '26px 1fr': 'channel',
  '1fr 44px 44px 44px 110px': 'checks',
  '110px 1fr 80px 80px': 'hours',
  '120px 1fr 70px 70px 80px': 'keys',
  '36px 1fr 1fr': 'tabs',
  '70px 1fr 1fr': 'deliveries',
  '1fr 1fr 1fr': 'three',
  '80px 1fr 60px': 'roster',
  '1fr 1fr 28px 70px': 'roll',
};

/** The drawn QR: the code fills the drawing's square (print.css says why its id is not on it). */
const drawnQr = (code: string): ReactNode => (
  <img
    src={`data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(code, { border: 1 }))}`}
    alt=""
  />
);

/** Money on the drawn paper: `$ 387,728.12`. */
export const paperMoney = (cents: number): string => formatMoney(cents).replace('$', '$ ');
const text = (c: PaperCell): string => (typeof c === 'number' ? paperMoney(c) : c);

/** The cards a page may print on are their own named pages in print.css (`@page card`). */
export function DrawnPages({
  pages,
  size = 'letter',
  stamp = 'day',
}: {
  pages: readonly PaperPage[];
  size?: SheetSize;
  /** The footer's "printed …": the day for a document, the day and time for Stream A's paper. */
  stamp?: 'day' | 'time';
}): ReactNode {
  const printed =
    // Paper's stamp reads `Sat Sep 11 · 11:52 AM`, with no comma after the day.
    stamp === 'time'
      ? formatDateTime(new Date()).replace(/^(\w{3}),/, '$1')
      : formatDate(new Date(), { year: true });
  return (
    <div className="pr-root pr-docs" data-size={size} data-pages={pages.length}>
      <style>{`@page { size: ${size === 'poster' ? '11in 17in' : 'letter'}; margin: 0; }`}</style>
      {/* Its place keeps a page's key unique where two pages share a title. */}
      {pages.map((p, n) => (
        <Sheet
          key={`${String(n)} ${p.title}${p.qr ?? ''}`}
          size={p.size ?? size}
          title={p.title}
          sub={p.sub}
          qr={p.qr ? { drawing: drawnQr(p.qr), code: p.qr } : undefined}
          name={p.foot}
          printed={printed}
        >
          {p.big !== undefined ? (
            <div className="pr-big-line">
              <span className="pr-big">{text(p.big)}</span>
              {p.bigSub ? <span className="pr-big-sub">{p.bigSub}</span> : null}
            </div>
          ) : null}
          {p.blocks.map((b, i) => (
            <div key={`${b.head}${String(i)}`} className="pr-block">
              {b.head ? <h2 className="pr-block__head">{b.head}</h2> : null}
              {b.rows.map((r, j) => (
                <div key={j} className="pr-block__row" data-cols={COLS[b.cols] ?? 'one'}>
                  {r.map((c, k) => (
                    <span key={k}>{text(c)}</span>
                  ))}
                </div>
              ))}
              {b.gap ? (
                <p className="pr-block__gap" role="alert">
                  {b.gap}
                </p>
              ) : null}
            </div>
          ))}
          {p.sign?.length ? (
            <div className="pr-doc-signs">
              {p.sign.map((s, i) => (
                <span key={`${s}${String(i)}`}>{s}</span>
              ))}
            </div>
          ) : null}
        </Sheet>
      ))}
    </div>
  );
}
